import { EinzaGraphiQLService } from "@/services/einzaGraphiQL"

import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { calculateReturnedPalletsPerOrder } from "../utils/calculateReturnedPallets"

export const queryOrdersPaletteStats = {
  async ordersPaletteStats(_parent, args, context, _info) {
    let { customerNumber, year = new Date().getFullYear().toString() } = args
    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.ORDERS,
      operation: Operations.READ,
    })

    await validateAccessRightsToCustomer({
      user: user,
      customerNumber: customerNumber,
    })

    const [returnedPalletsStats, totalPalletsStats] = await Promise.all([
      getReturnedPallets(customerNumber, year),
      getTotalPallets(customerNumber, year),
    ])

    const inPossession = totalPalletsStats.total - returnedPalletsStats.total
    const difference = inPossession - returnedPalletsStats.total
    let inPossessionOrders = []

    if (totalPalletsStats.orderStats.length !== 0) {
      inPossessionOrders = totalPalletsStats.orderStats.filter(
        (totalItem) =>
          !returnedPalletsStats.orderStats.some((returnedItem) => totalItem.orderNumber === returnedItem.orderNumber),
      )
    }
    // Calculate returned amounts for inPossessionOrders
    // Get return orders with items including description2
    const returnedPalletsWithItems = await getReturnedPalletsWithItems(customerNumber, year)

    const returnedPalletsMap = calculateReturnedPalletsPerOrder(returnedPalletsWithItems)

    // Add returnedAmount to each inPossessionOrder
    const inPossessionOrdersWithReturns = inPossessionOrders.map((order) => {
      const returnedData = returnedPalletsMap.get(order.shortOrderNumber)
      const returnedAmount = returnedData?.amount || 0
      return {
        ...order,
        returnedAmount,
      }
    })

    return {
      total: totalPalletsStats.total,
      returned: returnedPalletsStats.total,
      inPossession,
      difference,
      inPossessionOrders: inPossessionOrdersWithReturns,
      returnedOrders: returnedPalletsStats.orderStats,
    }
  },
}

/**
 * @param {string} customerNumber
 * @param {string} year
 */
const getReturnedPallets = async (customerNumber, year) => {
  const returnedFilters = [
    { field: "orderType", operator: "eq", value: "RÜ" },
    { field: "orderType", operator: "eq", value: "BR" },
  ]

  return fetchOrders(returnedFilters, year, customerNumber)
}

/**
 * Get return orders with full item details including description2
 * @param {string} customerNumber
 * @param {string} year
 * @returns {Promise<Array>} - Array of return orders with items
 */
const getReturnedPalletsWithItems = async (customerNumber, year) => {
  const returnedFilters = [
    { field: "orderType", operator: "eq", value: "RÜ" },
    { field: "orderType", operator: "eq", value: "BR" },
  ]

  let queries = []
  const generalFilters = formInitialFilters(year)

  for (const filter of returnedFilters) {
    const filters = [...generalFilters, filter]
    const graphiQLQueryObject = formQueryObject(customerNumber, filters)
    const mappedQuery = EinzaGraphiQLService.formQuery(graphiQLQueryObject)
    const customerOrdersResponse = EinzaGraphiQLService.getData(mappedQuery)

    queries.push(customerOrdersResponse)
  }

  const resolvedQueries = await Promise.all(queries)
  const allContent = resolvedQueries.reduce((acc, res) => {
    const content = res?.data?.orders?.content || []
    return acc.concat(content)
  }, [])
  return allContent
}

/**
 * @param {string} customerNumber
 * @param {string} year
 */
const getTotalPallets = async (customerNumber, year) => {
  const orderedFilters = [
    { field: "orderType", operator: "eq", value: "AU" },
    { field: "orderType", operator: "eq", value: "BA" },
    { field: "orderType", operator: "eq", value: "NL" },
  ]

  return fetchOrders(orderedFilters, year, customerNumber)
}

/**
 * @param {Array<{items: Array<{sku: ?string, amount: ?number}>}>} orders
 *
 * @returns {{total: number, orderStats: Array<{shortOrderNumber: string, orderNumber: string, amount: number, customerNumber: string}>}}
 */
const getPalletAmountFromOrdersContent = (orders) => {
  let total = 0
  /** @type {Array<{shortOrderNumber: string, orderNumber: string, amount: number, customerNumber: string}>} */
  let orderStats = []

  for (const content of orders) {
    if (content.items.length === 0) continue
    let orderAmount = 0

    for (const item of content.items) {
      if (!item?.sku || !item?.amount) continue
      if (item.sku !== "0040210") continue

      total += item.amount
      orderAmount += item.amount
    }

    const hasOrderTypeAndNumber = content?.orderType && content?.orderNumber

    orderStats.push({
      shortOrderNumber: hasOrderTypeAndNumber ? `${content?.orderType}-${content?.orderNumber2}` : "",
      orderNumber: content?.orderNumber || "",
      amount: orderAmount || 0,
      customerNumber: content?.customerNumber || "",
    })
  }

  return { total, orderStats }
}

/**
 * @param {Array<{field: string, operator: string, value: string}>} orderFilters
 * @param {string} year
 * @param {string} customerNumber
 * @returns {Promise<{total: number, orderStats: Array<{shortOrderNumber: string, orderNumber: string, amount: number, customerNumber: string}>}>}
 */
const fetchOrders = async (orderFilters, year, customerNumber) => {
  let queries = []
  const generalFilters = formInitialFilters(year)

  for (const filter of orderFilters) {
    const filters = [...generalFilters, filter]
    const graphiQLQueryObject = formQueryObject(customerNumber, filters)
    const mappedQuery = EinzaGraphiQLService.formQuery(graphiQLQueryObject)
    const customerOrdersResponse = EinzaGraphiQLService.getData(mappedQuery)

    queries.push(customerOrdersResponse)
  }

  const resolvedQueries = await Promise.all(queries)
  const allContent = resolvedQueries.reduce((acc, res) => {
    const content = res?.data?.orders?.content || []
    return acc.concat(content)
  }, [])

  if (allContent.length === 0) {
    return { total: 0, orderStats: [] }
  }
  return getPalletAmountFromOrdersContent(allContent)
}

/**
 * @param {string} year
 */
const formInitialFilters = (year) => {
  return [
    {
      field: "year",
      operator: "eq",
      value: year,
    },
    {
      field: "item.sku",
      operator: "eq",
      value: "0040210",
    },
    {
      field: "orderStatus",
      operator: "eq",
      value: "F",
    },
  ]
}

/**
 * @param {string} customerNumber
 * @param {Array<{field: string, operator: string, value: string}>} filters
 */
const formQueryObject = (customerNumber, filters) => {
  return [
    {
      method: "orders: findOrdersByCustomerAndFilter",
      variables: {
        customerNumber,
        filters,
        page: 0,
        size: 500,
      },
      fields: ["number", "numberOfElements", "totalElements", "totalPages"],
      expandedFields: {
        content: [
          "items { sku, amount, additionalDescription: description2 }",
          "orderNumber",
          "orderNumber2",
          "customerNumber",
          "orderType",
        ],
      },
    },
  ]
}
